// The single live connection to cmx-hub: token fetch, transport race, auto-reconnect with
// backoff, heartbeat/RTT and CONTROL request/reply correlation.

import { T, decode, encControl, encPing, type Decoded, type ServerEvent } from './protocol';
import { raceConnect, type Transport, type TransportKind } from './transport';
import { telemetry } from './telemetry';
import { store } from '../store';

export interface FrameHandlers {
  /** OUTPUT / SNAPSHOT / EXITED / INPUT_ACK frames. */
  onSessionFrame(f: Decoded): void;
  onEvent(e: ServerEvent): void;
  /** Called after every (re)connect, before any queued CONTROL is sent. */
  onConnected(): void;
  /** The connection's protocol version is known (first event: `hello` means v2). */
  onReady?(): void;
}

/** Durable input on the current connection: unknown until its first event arrives. */
export type InputMode = 'unknown' | 'v2' | 'v1';

interface TokenResponse {
  token: string;
  wt?: string;
  ws?: string;
}

class UnauthorizedError extends Error {}

const BACKOFF_MIN = 100;
const BACKOFF_MAX = 2000;

function withToken(raw: string, token: string): string {
  const u = new URL(raw, location.href);
  u.searchParams.set('token', token);
  return u.toString();
}

/** A WebSocket URL on this page's host. */
export function sameOriginWs(path = '/ws'): string {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${path}`;
}

export class Connection {
  private transport: Transport | null = null;
  private handlers: FrameHandlers | null = null;
  private running = false;
  private backoff = BACKOFF_MIN;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private connecting = false;
  private reqId = 1;
  private pending = new Map<
    number,
    { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }
  >();
  private waiters: Array<() => void> = [];
  private pingTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRx = 0;
  /** performance.now() when the frame being handled arrived (latency tracing). */
  get rxAt(): number {
    return this.lastRx;
  }
  private lastPingSent = 0;
  lastRtt: number | null = null;
  /** Number of successful connects (tests use it to observe reconnects). */
  connects = 0;
  /** Whether this connection speaks INPUT_SEQ (see SessionTerm.sendInput). */
  inputMode: InputMode = 'unknown';
  /** What the machine's agent said it does (hello): "desktop" when it has one, … */
  features: string[] = [];
  /** Test hook: swallow outgoing frames until the next drop (a dying connection). */
  private blackhole = false;
  /** Test hook: this transport went silent both ways (a network change or sleep); see sever(). */
  private severed: Transport | null = null;
  /** A liveness check is armed (see probe); watchNext: input sent while it ran. */
  private watchNext = 0;
  private watchTimer: ReturnType<typeof setTimeout> | null = null;

  setHandlers(h: FrameHandlers) {
    this.handlers = h;
  }

  get kind(): TransportKind | null {
    return this.transport?.kind ?? null;
  }

  get connected(): boolean {
    return this.transport !== null;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.backoff = BACKOFF_MIN;
    store.set({ conn: 'connecting', connError: null });
    telemetry.start();
    addEventListener('online', this.onNetwork);
    document.addEventListener('visibilitychange', this.onVisibility);
    netInfo()?.addEventListener('change', this.onNetwork);
    void this.connect();
  }

  stop() {
    this.running = false;
    removeEventListener('online', this.onNetwork);
    document.removeEventListener('visibilitychange', this.onVisibility);
    netInfo()?.removeEventListener('change', this.onNetwork);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.stopPing();
    this.transport?.close();
    this.transport = null;
  }

  /** Test hook: abruptly drop the current transport (the reconnect path takes over). */
  drop() {
    this.blackhole = false;
    this.transport?.close();
  }

  /** Test hook: frames sent from now on are lost on the way (until drop()). */
  loseSends() {
    this.blackhole = true;
  }

  /** Test hook: the current transport goes silent both ways, like a network change or sleep. */
  sever() {
    this.severed = this.transport;
  }

  /** Send a frame now. Returns false if there is no live transport (the frame is dropped). */
  send(frame: Uint8Array): boolean {
    const t = this.transport;
    if (!t) return false;
    if (!this.blackhole && t !== this.severed) t.send(frame);
    return true;
  }

  /**
   * Input just went out: the machine answers every keystroke (INPUT_ACK, an echo) within a
   * round trip, so a connection that stays silent for a few round trips after it is dead
   * (a network change, a laptop waking up). Reconnect then instead of waiting for pings.
   */
  expectReply() {
    this.probe(false);
  }

  /** Arm the liveness check (a ping too, unless input is on its way that the agent answers). */
  private probe(ping = true) {
    const t = this.transport;
    if (!t) return;
    const now = performance.now();
    if (ping || this.inputMode !== 'v2') this.send(encPing(now));
    if (this.watchTimer) {
      this.watchNext = now; // checked when the running check ends
      return;
    }
    this.watch(t, now, this.probeWait());
  }

  private probeWait(): number {
    return Math.min(1500, Math.max(300, 4 * (this.lastRtt ?? 100) + 150));
  }

  /** Something must arrive on t after `since`, within `wait` ms, or t is dead. */
  private watch(t: Transport, since: number, wait: number) {
    this.watchTimer = setTimeout(() => {
      this.watchTimer = null;
      if (this.transport !== t) return;
      if (this.lastRx < since) {
        console.info(`cmx: no reply for ${Math.round(performance.now() - since)} ms, reconnecting`);
        t.close();
        return;
      }
      // Answered; but something sent since then may not be yet.
      const next = this.watchNext;
      this.watchNext = 0;
      if (next > since && this.lastRx < next) this.watch(t, next, Math.max(0, next + this.probeWait() - performance.now()));
    }, wait);
  }

  /** CONTROL request; waits for a connection if needed. */
  async control<R = unknown>(op: string, fields: Record<string, unknown> = {}, timeoutMs = 15_000): Promise<R> {
    if (!this.transport) await this.whenConnected(timeoutMs);
    const id = this.reqId++;
    return new Promise<R>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`${op}: timed out`));
      }, timeoutMs);
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      if (!this.send(encControl({ id, op, ...fields }))) {
        clearTimeout(timer);
        this.pending.delete(id);
        reject(new Error(`${op}: not connected`));
      }
    });
  }

  private whenConnected(timeoutMs: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.waiters = this.waiters.filter((w) => w !== done);
        reject(new Error('not connected'));
      }, timeoutMs);
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      this.waiters.push(done);
    });
  }

  private kick = () => {
    if (!this.running || this.transport || this.connecting) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.backoff = BACKOFF_MIN;
    void this.connect();
  };

  /** "Try now": skip the backoff wait and connect. */
  retryNow() {
    this.kick();
  }

  private onVisibility = () => {
    if (document.visibilityState === 'visible') {
      if (!this.transport) this.kick();
      else this.probe();
    }
  };

  /** The network changed (or came back): check the connection now rather than at the next ping. */
  private onNetwork = () => {
    if (!this.transport) this.kick();
    else this.probe();
  };

  private async fetchToken(): Promise<TokenResponse> {
    const r = await fetch('/api/token', { credentials: 'same-origin', cache: 'no-store' });
    if (r.status === 401) throw new UnauthorizedError('unauthorized');
    if (!r.ok) throw new Error(`token: HTTP ${r.status}`);
    return (await r.json()) as TokenResponse;
  }

  private async connect() {
    if (!this.running || this.connecting) return;
    this.connecting = true;
    try {
      const tok = await this.fetchToken();
      // In dev (Vite proxy) and whenever the hub gives no URL, use this origin for WebSocket.
      const wsBase = !import.meta.env.DEV && tok.ws ? tok.ws : sameOriginWs();
      const t = await raceConnect({
        wt: tok.wt ? withToken(tok.wt, tok.token) : null,
        ws: withToken(wsBase, tok.token),
        headStartMs: 300,
      });
      if (!this.running) {
        t.close();
        return;
      }
      this.attach(t);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        this.running = false;
        store.set({ conn: 'unauthorized', connError: null });
        return;
      }
      store.set({ conn: 'reconnecting', connError: e instanceof Error ? e.message : String(e) });
      this.scheduleReconnect();
    } finally {
      this.connecting = false;
    }
  }

  private scheduleReconnect() {
    if (!this.running || this.retryTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(BACKOFF_MAX, this.backoff * 2);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, delay);
  }

  private attach(t: Transport) {
    this.transport = t;
    this.connects++;
    this.inputMode = 'unknown';
    this.backoff = BACKOFF_MIN;
    this.lastRx = performance.now();
    telemetry.transport = t.kind;
    t.onFrame = this.onFrame;
    t.onClose = (reason) => {
      if (this.transport !== t) return;
      this.transport = null;
      if (this.watchTimer) clearTimeout(this.watchTimer);
      this.watchTimer = null;
      this.inputMode = 'unknown';
      this.stopPing();
      for (const [, p] of this.pending) {
        clearTimeout(p.timer);
        p.reject(new Error('disconnected'));
      }
      this.pending.clear();
      store.set({ conn: 'reconnecting', connError: reason });
      this.scheduleReconnect();
    };
    store.set({ conn: 'connected', transport: t.kind, connError: null });
    this.handlers?.onConnected();
    const ws = this.waiters;
    this.waiters = [];
    ws.forEach((w) => w());
    this.schedulePing(0);
  }

  private onFrame = (buf: Uint8Array) => {
    if (this.severed && this.transport === this.severed) return; // test hook: gone silent
    this.lastRx = performance.now();
    let f: Decoded | null;
    try {
      f = decode(buf);
    } catch (e) {
      console.warn('cmx: bad frame', e);
      return;
    }
    if (!f) return;
    switch (f.type) {
      case T.OUTPUT:
      case T.SNAPSHOT:
      case T.EXITED:
      case T.INPUT_ACK:
        this.handlers?.onSessionFrame(f);
        break;
      case T.PONG: {
        if (!Number.isFinite(f.ts)) break;
        const rtt = performance.now() - f.ts;
        if (rtt >= 0 && rtt < 60_000) {
          this.lastRtt = rtt;
          telemetry.recordRtt(rtt);
          store.set({ rtt: Math.round(rtt * 10) / 10 });
        }
        break;
      }
      case T.CONTROL_REPLY: {
        const p = this.pending.get(f.reply.id);
        if (!p) break;
        this.pending.delete(f.reply.id);
        clearTimeout(p.timer);
        if (f.reply.ok) p.resolve(f.reply.result ?? {});
        else p.reject(new Error(f.reply.error || 'request failed'));
        break;
      }
      case T.EVENT:
        if (this.inputMode === 'unknown') {
          // v2 agents always send `hello` first; anything else means a v1 agent.
          this.inputMode = f.event.kind === 'hello' && f.event.version >= 2 ? 'v2' : 'v1';
          this.features = f.event.kind === 'hello' ? (f.event.features ?? []) : [];
          this.handlers?.onReady?.();
        }
        this.handlers?.onEvent(f.event);
        break;
      default:
        break;
    }
  };

  // --- heartbeat --------------------------------------------------------------------------

  private interval(): number {
    return store.get().focusedSession !== null ? 2000 : 5000;
  }

  private stopPing() {
    if (this.pingTimer) clearTimeout(this.pingTimer);
    this.pingTimer = null;
  }

  private schedulePing(delay: number) {
    this.stopPing();
    this.pingTimer = setTimeout(this.pingTick, delay);
  }

  private pingTick = () => {
    this.pingTimer = null;
    const t = this.transport;
    if (!t) return;
    const iv = this.interval();
    if (document.visibilityState === 'visible') {
      const now = performance.now();
      // Dead-connection detection: nothing received for 3 intervals after pinging.
      if (this.lastPingSent && now - this.lastRx > Math.max(3 * iv, 6000) && this.lastPingSent > this.lastRx) {
        console.info('cmx: connection silent, reconnecting');
        t.close();
        return;
      }
      this.lastPingSent = now;
      t.send(encPing(now));
    }
    this.schedulePing(iv);
  };
}

/** The Network Information API (Chromium): fires 'change' when the network changes. */
function netInfo(): EventTarget | undefined {
  return (navigator as Navigator & { connection?: EventTarget }).connection;
}

export const conn = new Connection();
